import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import { Rocket } from "lucide-react";
import { useTranslation } from "react-i18next";

const emailSchema = z.object({
  email: z.string().email("Please enter a valid email address").max(255),
});

const LaunchBanner = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    const validation = emailSchema.safeParse({ email: email.trim() });
    
    if (!validation.success) {
      toast({
        title: t('launchBanner.invalidEmail'),
        description: validation.error.errors[0].message,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase
        .from("email_waitlist")
        .insert([{ email: validation.data.email }]);

      if (error) {
        if (error.code === "23505") {
          toast({
            title: t('launchBanner.alreadySignedUp'),
            description: t('launchBanner.alreadySignedUpDesc'),
          });
        } else {
          throw error;
        }
      } else {
        toast({
          title: t('launchBanner.successTitle'),
          description: t('launchBanner.successDesc'),
        });
        setEmail("");
      }
    } catch (error) {
      console.error("Error signing up:", error);
      toast({
        title: t('launchBanner.errorTitle'),
        description: t('launchBanner.errorDesc'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed top-16 md:top-20 left-0 right-0 z-40 bg-gradient-primary py-3 px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
      <div className="container relative z-10">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
          <div className="flex items-center gap-2">
            <Rocket className="w-5 h-5 text-primary-foreground animate-pulse" />
            <div>
              <p className="text-primary-foreground font-bold text-sm sm:text-base">
                🎉 Dashboard v1.0 Now Live! Live Performance Analytics for Premium Members
              </p>
            </div>
          </div>
          <Button
            variant="secondary"
            className="whitespace-nowrap text-sm px-6"
            asChild
          >
            <a href="https://dashboard.oraculusx.com" target="_blank" rel="noopener noreferrer">
              View Dashboard
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LaunchBanner;
